import { GroupType } from '../../PandoraSolutionBuilderAgent/model/enumerations';
import { EventCodes } from '../../../App/model/event-codes';

SearchProviderService.$inject = [
	'$stateParams',
	'mx.shell.Config',
	'mx.shell.EventService',
	'mx.shell.SidebarService',
	'mx.shell.View',
	'mx.internationalization',
	'mx.shell.RoutingService',
	'mx.SolutionBuilderAgent.SearchQueryGroup',
];

/**
 * @ngdoc service
 * @name mx.shell.SearchProviderService
 * @module mx.shell
 *
 * @description
 * A service provides methods to manage search and search related tasks.
 */
export function SearchProviderService($stateParams, config, eventService, sidebarService, MxView, i18n, routingService, SearchQueryGroup) {
	const maxRecentSearches = (config.get('search') || {}).maxRecentSearches || 5;
	const recentQueriesStorageKey = 'mx.search.rq:';
	const searchFormStorageKey = 'mx.search.sf:';

	let builderView;

	let _currentSearch = {
		searchForm: new SearchQueryGroup(),
	};
	let contexts = [];
	let customDataSetContext;
	let globalSearchParametersPromise;

	let searchExecuting = false;
	const uiToken = 'uiToken:search-page-content';
	let searchText = '';
	let unifiedSearchExtraConfigFromComponent = {
		enabled: false,
		navigationItems: [],
	};

	const globalSearchContext = initGlobalSearchContext();

	eventService.subscribeGlobal(mx.shell.EventCodes.RouteLoading, onRouteLoading);
	eventService.subscribeGlobal(mx.shell.EventCodes.CurrentNavigationItemChanged, (e, { navigationItem }) => onNavigationItemChanged(navigationItem));
	eventService.subscribeGlobal(mx.shell.EventCodes.NavigationChildrenLoaded, (e, { children }) => setContextsFromNavigationItems(children));
	eventService.subscribeGlobal(mx.shell.EventCodes.NavigationLoaded, (e, { navItems }) => setContextsFromNavigationItems(navItems));

	const self = {
		performSearch: performSearch,
		getCurrentGlobalSearchParameters: getCurrentGlobalSearchParameters,
		getGlobalSearchContext: getGlobalSearchContext,
		isGlobalSearchRoute: isGlobalSearchRoute,
		isUnifiedSearchVisible: isUnifiedSearchVisible,
		showUnifiedSearch: showUnifiedSearch,
		hideUnifiedSearch: hideUnifiedSearch,

		runSearch: runSearch,
		currentContext: currentContext,
		setContextsFromNavigationItems: setContextsFromNavigationItems,
		getContexts: getContexts,
		currentSearch: currentSearch,
		getCurrentSearchPageSearchOptions: getCurrentSearchPageSearchOptions,
		openAdvancedSearch: openAdvancedSearch,
		openSearchQueryBuilder: openSearchQueryBuilder,
		getQueryOperatorText: getQueryOperatorText,
		getBaseQueryId: getBaseQueryId,

		resetSearch: resetSearch,
		resetSearchParameters: resetSearchParameters,
		resetAdvancedSearch: resetAdvancedSearch,
		resetKeywordSearch: resetKeywordSearch,
		resetGridSearch: resetGridSearch,
		resetCustomFormSearch: resetCustomFormSearch,
		setCustomDataSetContext: setCustomDataSetContext,
		uiToken: undefined,
	};

	Object.defineProperty(self, 'searchText', {
		enumerable: true,
		get: function () {
			return searchText;
		},
	});

	Object.defineProperty(self, 'recentQueriesStorageKey', {
		writable: false,
		value: recentQueriesStorageKey,
	});
	Object.defineProperty(self, 'searchFormStorageKey', {
		writable: false,
		value: searchFormStorageKey,
	});
	Object.defineProperty(self, 'maxRecentSearches', {
		writable: false,
		value: maxRecentSearches,
	});

	return self;

	function getGlobalSearchContext() {
		return globalSearchContext;
	}

	async function getContextsFromStateParams(allContexts, stateParams, checkSelection = false) {
		if (typeof stateParams !== 'string' || stateParams.length === 0) {
			return [];
		}
		const contexts = [];
		(stateParams || '')
			.toLowerCase()
			.split(',')
			.filter((navigationId) => navigationId.length > 0)
			.map((stateContextString) => {
				const selected = stateContextString[0] !== '!';
				const navigationIdString = stateContextString.replace('!', '');
				const stateNavigationIds = navigationIdString.split('_');
				const stateNavigationId = stateNavigationIds[stateNavigationIds.length - 1];

				let stateContexts = allContexts.filter((context) => navigationIdString === context.parameters.navigationId);
				let context;

				if (stateContexts.length === 1) {
					// context for standard dataset without filters and structures
					context = stateContexts[0];
				} else if (stateContexts.length > 1) {
					//contexts for filters: state = navigationId
					context = stateContexts.find((stateContext) => stateContext.parameters.filterId === '');
				} else if (stateContexts.length === 0) {
					stateContexts = allContexts.filter((context) => stateNavigationId === context.parameters.navigationId);
					if (stateContexts.length > 1) {
						//contexts for filters: state = filterId_navigationId
						context = stateContexts.find((stateContext) => stateContext.parameters.filterId === stateNavigationIds[0]);
					} else {
						// for structure dynamic navigation items which was not loaded at the moment
						const rootStructureContext = stateContexts[0];
						if (rootStructureContext) {
							if (rootStructureContext.navigationItem.childrenDynamicLoader) {
								const contextLoader = rootStructureContext.navigationItem.childrenDynamicLoader().then((/*children*/) => {
									const context = getContexts(true).find((context) => navigationIdString === context.parameters.navigationId);
									if (checkSelection && context) {
										context.selected = selected;
									}
									return context;
								});
								contexts.push(contextLoader);
							} else {
								context = rootStructureContext;
							}
						}
					}
				}

				if (!context) {
					return null;
				}

				if (checkSelection) {
					context.selected = selected;
				}
				contexts.push(context);
			});
		return await Promise.all(contexts);
	}

	function getStateParamFromContexts(contexts, checkSelection = false) {
		return contexts
			.map((context) => {
				const filterString = context.parameters.filterType === 'filter' && context.parameters.filterId ? `${context.parameters.filterId}_` : '';
				return (!checkSelection || context.selected ? '' : '!') + filterString + context.parameters.navigationId;
			})
			.join(',');
	}

	async function getCurrentGlobalSearchParameters(ignoreManager) {
		globalSearchParametersPromise = globalSearchParametersPromise || _getCurrentGlobalSearchParameters();
		const parameters = await globalSearchParametersPromise;

		if (!ignoreManager && unifiedSearchExtraConfigFromComponent.enabled) {
			// available contexts on pages where global-search-manager is added with configured contexts
			// just contexts configured in LayoutDesigner should be available
			// Ex.: SSP => Workplaces, SSP => Home => Management Area
			const contexts = unifiedSearchExtraConfigFromComponent.navigationItems.map((navigationItem) => navigationItem.searchContext);
			return Object.assign({}, parameters, {
				navigationId: $stateParams.navigationId,
				contexts: contexts,
				filteredContexts: contexts,
			});
		}

		return parameters;
	}

	async function _getCurrentGlobalSearchParameters() {
		const allContexts = getContexts(true);
		allContexts.forEach((context) => (context.selected = false));
		const navigationId = $stateParams.navigationId;
		const extraContexts = await getContextsFromStateParams(allContexts, $stateParams.extraContexts);
		const stateContexts = await getContextsFromStateParams(allContexts, $stateParams.contexts, true);

		const parameters = {
			keyword: $stateParams.keyword || '',
			contexts: extraContexts.concat(getGlobalSearchContexts()),
			extraContexts: extraContexts,
			isGlobal: isGlobalSearchRoute(),
			currentContext: currentContext(true),
		};

		if (parameters.isGlobal) {
			if (navigationId && stateContexts.length) {
				// 1. available contexts on global-search page which is opened after performing search from option 1.
				parameters.filteredContexts = parameters.contexts = stateContexts;
				parameters.navigationId = navigationId;
			} else if (stateContexts.length) {
				// 2. available contexts on standard global-search page (opened not from option 1)
			}
		} else if (parameters.currentContext) {
			// 3. available contexts for toolbar-search or unified-search on any dataset
			if (!parameters.contexts.includes(parameters.currentContext)) {
				parameters.contexts.push(parameters.currentContext);
				parameters.extraContexts.push(parameters.currentContext);
			}
			parameters.currentContext.selected = true;
		}

		return parameters;
	}

	function initGlobalSearchContext() {
		const currentStateParameters = routingService.getCurrent().parameters;

		if (currentStateParameters.keyword) {
			_currentSearch.keyword = currentStateParameters.keyword;
		}

		const parameters = {
			appString: currentStateParameters.appString,
			keyword: currentStateParameters.keyword,
			contexts: currentStateParameters.contexts,
			navigationId: null,
		};

		return new mx.shell.SearchContext({
			name: 'Global_Search',
			title: i18n.get('shell.mx-search.noContext'),
			parameters: parameters,
			routeName: 'globalSearch',
		});
	}

	function getContextTitle(navigationItem) {
		if (!navItemIsDynamic(navigationItem)) {
			return navigationItem.title;
		}

		let title = '';

		while (navigationItem) {
			title = navigationItem.title + (title ? ' > ' : '') + title;

			if (!navItemIsDynamic(navigationItem)) {
				break;
			}

			navigationItem = navigationItem.parent;
		}

		return title;
	}

	function onRouteLoading() {
		globalSearchParametersPromise = null;
	}

	async function onNavigationItemChanged() {
		if (searchExecuting) {
			return;
		}

		const searchParameters = await getCurrentGlobalSearchParameters();

		if (!searchParameters.currentContext) {
			self.currentSearch(null);
		} else {
			const searchContext = searchParameters.currentContext;
			const filters = searchContext.parameters.filterId ? [searchContext.parameters.filterId] : [];
			let filterType = searchContext.parameters.filterType ? searchContext.parameters.filterType : '';

			if ($stateParams.extraFilter && !filters.includes($stateParams.extraFilter)) {
				filters.push($stateParams.extraFilter);
				filterType = 'filter';
			}

			self.currentSearch({
				keyword: searchParameters.keyword,
				context: searchContext,
				uiToken: uiToken,
				filters: filters,
				filterType: filterType,
			});
		}
	}

	async function showUnifiedSearch(navigationItems = []) {
		unifiedSearchExtraConfigFromComponent = {
			enabled: Array.isArray(navigationItems) && navigationItems.length > 0,
			navigationItems,
		};

		eventService.dispatchEvent(mx.shell.EventCodes.GlobalSearchConfigChanged);
	}

	function hideUnifiedSearch() {
		unifiedSearchExtraConfigFromComponent = {
			enabled: false,
			navigationItems: [],
		};
	}

	function isUnifiedSearchVisible() {
		return unifiedSearchExtraConfigFromComponent.enabled || isGlobalSearchRoute();
	}

	function isGlobalSearchRoute() {
		return routingService.getCurrent().name === 'globalSearch';
	}

	async function performSearch(newSearchText, newSelectedContexts) {
		if (!newSearchText || newSearchText.length < 3 || searchExecuting) {
			return;
		}
		searchExecuting = true;
		newSelectedContexts = Array.isArray(newSelectedContexts) ? newSelectedContexts : [];
		searchText = newSearchText;

		const curContext = currentContext(true);

		const { filteredContexts, contexts, navigationId, extraContexts } = await getCurrentGlobalSearchParameters();

		const selectedContexts = (filteredContexts || contexts).filter((context) => {
			context.selected = newSelectedContexts.indexOf(context) >= 0;
			return context.selected;
		});

		const newContext = curContext !== globalSearchContext && selectedContexts.length === 1 && curContext === selectedContexts[0] ? curContext : globalSearchContext;

		newContext.parameters.keyword = searchText;
		/*
			if context is the same then just execute search with new keyword in search options,
			otherwise set new options for search (keyword and also filters, filterType) and then change navigation.
			After navigation is changed new search page (mx-search-page-content) will load data with new options
		*/
		if (newContext === curContext) {
			const searchOptions = self.currentSearch();
			searchOptions.keyword = searchText;
			self.currentSearch(searchOptions);
			eventService.dispatchEvent(mx.shell.EventCodes.Search, searchOptions);
			searchExecuting = false;
		} else {
			newContext.parameters.contexts = getStateParamFromContexts(filteredContexts || selectedContexts, true);
			newContext.parameters.extraContexts = getStateParamFromContexts(extraContexts);
			newContext.parameters.navigationId = navigationId;

			const searchOptions = {
				globalSearch: true,
				keyword: searchText,
				context: newContext,
				uiToken: uiToken,
				filters: newContext.parameters && newContext.parameters.filterId ? [newContext.parameters.filterId] : [],
				filterType: (newContext.parameters && newContext.parameters.filterType) || '',
			};
			self.currentSearch(searchOptions);
			routingService.go(newContext.routeName, angular.copy(newContext.parameters)).then(function () {
				searchExecuting = false;
			});
		}
	}

	function openAdvancedSearch(params) {
		const context = (params && params.context) || self.currentContext();

		self.uiToken = params && params.uiToken;
		const searchView = new MxView({
			id: '__advanced_search_view__',
			name: i18n.get('solutionBuilderAgent.components.mx-advanced-search.searchIn') + ' ' + (context && context.title),
			locals: {
				context: context,
				uiToken: self.uiToken,
				dataQueryList: params.dataQueryList,
			},
			controller: function () {},
			template: `<mx-advanced-search context="vm.context" data-query-list="vm.dataQueryList" ui-token="{{vm.uiToken}}"></mx-advanced-search>`,
		});

		eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: `Advanced search opened`,
			sender: params?.dataQueryList?.parameters.id,
			senderTitle: params?.dataQueryList?.parameters.name,
			contextTypeId: params?.dataQueryList?.parameters.entities.map((entity) => entity.id),
			contextTypeTitle: params?.dataQueryList?.parameters.entities.map((entity) => entity.name),
		});

		sidebarService.open(searchView);
	}

	function getQueryOperatorText(value, defaultValue) {
		let result = defaultValue || 'and';

		if (value) {
			result = value === mx.SolutionBuilderAgent.SearchQueryLogicalOperator.OR || (value.toLowerCase && value.toLowerCase() === 'or') ? 'or' : 'and';
		}

		return result;
	}

	function openSearchQueryBuilder(params) {
		builderView = new MxView({
			id: '__search_query_builder__',
			name: params.title || i18n.get('solutionBuilderAgent.components.mx-search-query-builder.editQuery'),
			locals: {
				query: params.query,
				context: params.context,
				parent: params.parent,
				showInNavigation: params.showInNavigation,
				uiToken: self.uiToken,
				dataQueryList: params.dataQueryList,
			},
			controller: function () {
				const vm = this;

				vm.closePanel = () => {
					openAdvancedSearch({
						uiToken: vm.uiToken,
						context: vm.context,
						dataQueryList: vm.dataQueryList,
					});
				};
			},
			template: `
				<mx-search-query-builder
					query="vm.query"
					context="vm.context"
					close-fn="vm.closePanel()"
					show-in-navigation="vm.showInNavigation"
					data-query-list="vm.dataQueryList"
					parent="vm.parent">
				</mx-search-query-builder>`,
		});
		sidebarService.open(builderView);
	}

	function resetKeywordSearch() {
		const currentSearch = self.currentSearch();
		currentSearch.keyword = '';
		runSearch(currentSearch);
	}

	function resetGridSearch() {
		const currentSearch = self.currentSearch();

		currentSearch.searchForm.searchText = '';
		currentSearch.searchForm.setGroup(new SearchQueryGroup(), GroupType.GridSearch);

		runSearch(currentSearch);
	}

	function resetAdvancedSearch() {
		const currentSearch = self.currentSearch();
		currentSearch.searchForm.setGroup(new SearchQueryGroup(), GroupType.QueryBuilder);
		runSearch(currentSearch);
	}

	function resetCustomFormSearch() {
		const currentSearch = self.currentSearch();

		currentSearch.searchForm.customFormConditions.length = 0;

		runSearch(currentSearch);
	}

	function resetSearchParameters() {
		const currentSearch = self.currentSearch();
		const currentContext = self.currentContext();
		let baseFilter;

		currentSearch.keyword = '';
		currentSearch.searchForm = new SearchQueryGroup();

		if (currentContext) {
			baseFilter = self.getBaseQueryId(currentContext.parameters);
		}
		currentSearch.filters = baseFilter ? [baseFilter] : [];
		return currentSearch;
	}

	function resetSearch() {
		const currentSearch = resetSearchParameters();
		runSearch(currentSearch);
	}

	function runSearch(options) {
		const searchOptions = options || self.currentSearch();

		if (searchOptions.context) {
			const newOptions = {
				keyword: searchOptions.keyword || '',
				context: searchOptions.context,
				filters: searchOptions.filters || [],
				filtersOperator: searchOptions.filtersOperator || mx.SolutionBuilderAgent.SearchQueryLogicalOperator.AND,
				searchForm: searchOptions.searchForm,
				uiToken: searchOptions.uiToken,
				userId: searchOptions.userId,
				dataQueryList: searchOptions.dataQueryList,
				dontHideCustomForm: searchOptions.dontHideCustomForm,
			};

			self.currentSearch(newOptions);
			eventService.dispatchEvent(mx.shell.EventCodes.Search, newOptions);
		}
	}

	function setCustomDataSetContext(context) {
		customDataSetContext = context;
	}

	function currentContext() {
		const currentState = routingService.getCurrent();
		if (currentState.name === 'customDataSet') {
			return customDataSetContext;
		} else {
			customDataSetContext = null;
		}

		let { navigationId, filterType, filterId } = currentState.parameters;
		const contexts = getContexts(true);

		return contexts.find((ctx) => ctx.parameters.navigationId === navigationId && ctx.parameters.filterId === filterId && ctx.parameters.filterType === filterType);
	}

	function setContextsFromNavigationItems(navItems) {
		if (Array.isArray(navItems)) {
			navItems.forEach((navItem) => addSearchContext(navItem));
			contexts.sort(({ title: name1 }, { title: name2 }) => (name1 > name2 ? 1 : name1 < name2 ? -1 : 0));
		}
	}

	function navItemIsDynamic(navigationItem) {
		return (navigationItem.parameters?.filterType === 'structure' && navigationItem.parameters?.filterId) || navigationItem.parameters?.filterType === 'filter';
	}

	function createSearchContext(navigationItem) {
		return new mx.SolutionBuilderAgent.SearchContext({
			name: navigationItem.name,
			title: getContextTitle(navigationItem),
			routeName: navigationItem.routingItem.name || navigationItem.routingItem,
			parameters: navigationItem.parameters,
			showInGlobalSearch: navigationItem.availableInSearch,

			dataQuery: navigationItem.dataQuery,
			widgetId: navigationItem.contentWidgetId,
			navigationItem: navigationItem,
		});
	}

	function addSearchContext(navigationItem) {
		if (navigationItem.routingItem && navigationItem.routingItem.name === 'searchPage') {
			const ctx = createSearchContext(navigationItem);
			const contextExist = contexts.find(
				({ parameters: { navigationId, filterId, filterType } }) =>
					navigationId === ctx.parameters.navigationId && filterId === ctx.parameters.filterId && filterType === ctx.parameters.filterType
			);

			if (!contextExist) {
				contexts.push(ctx);
				navigationItem.searchContext = ctx;
			} else {
				navigationItem.searchContext = contextExist;
			}
		}

		if (Array.isArray(navigationItem.children)) {
			navigationItem.children.forEach((childItem) => {
				// For Structures used in navigation this property is undefined.
				if (!childItem.dataQuery) {
					childItem.dataQuery = navigationItem.dataQuery;
				}
				addSearchContext(childItem);
			});
		}
	}

	function getCurrentSearchPageSearchOptions() {
		const currentContext = self.currentContext();
		const searchOptions = self.currentSearch();

		if (searchOptions && searchOptions.context === currentContext) {
			return searchOptions;
		}

		return {
			context: currentContext,
			filters: currentContext && currentContext.parameters && currentContext.parameters.filterId ? [currentContext.parameters.filterId] : [],
			filterType: (currentContext && currentContext.parameters && currentContext.parameters.filterType) || '',
		};
	}

	function getContexts(withFiltersContexts = false) {
		return withFiltersContexts ? contexts.slice() : contexts.filter((context) => !context.navigationItem.__fromFilter);
	}

	function getGlobalSearchContexts() {
		return getContexts(true).filter((item) => item.showInGlobalSearch);
	}

	function currentSearch(searchOptions) {
		if (searchOptions) {
			searchOptions.searchForm = searchOptions.searchForm || new SearchQueryGroup();
			_currentSearch = searchOptions;
		}
		return _currentSearch;
	}

	function getBaseQueryId(targetParams) {
		const currentRouteParameters = routingService.getCurrent()?.parameters;
		const getFilterId = (parameters) => (parameters?.filterType === 'filter' && parameters?.filterId) || '';
		const currentRouteFilterId = getFilterId(currentRouteParameters);

		return !currentRouteFilterId || (currentRouteFilterId && targetParams?.navigationId && targetParams?.navigationId !== currentRouteParameters?.navigationId)
			? getFilterId(targetParams)
			: currentRouteFilterId;
	}
}
